import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Icon, type IconName } from '~/components/ui/icon.tsx';
import { cn } from '~/styles/extended-theme.ts';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&:has(svg)]:pl-11 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'bg-destructive text-destructive-foreground [&>svg]:text-destructive-foreground',
        warning: 'bg-warning text-warning-foreground [&>svg]:text-warning-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type Props = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants> & {
    title: string;
    description?: string;
    icon?: IconName;
    titleClassName?: string;
    descriptionClassName?: string;
  };

const Alert = React.forwardRef<HTMLDivElement, Props>(
  (
    { className, variant, title, description, icon, titleClassName, descriptionClassName, ...props },
    ref,
  ) => (
    <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      <h5 className={cn('mb-1 font-medium leading-none tracking-tight', titleClassName)}>
        {icon ? <Icon name={icon} /> : null}
        {title}
      </h5>
      {description ? (
        <div className={cn('text-sm [&_p]:leading-relaxed', descriptionClassName)}>
          {description}
        </div>
      ) : null}
    </div>
  ),
);

Alert.displayName = 'Alert';

export { Alert };
